import React, { useEffect, useState } from "react";
import {
  MdOutlineBadge,
  MdOutlineEmojiEvents,
  MdOutlineImage,
  MdOutlineLeaderboard,
  MdOutlinePersonAdd,
  MdOutlineWavingHand,
} from "react-icons/md";
import { getUserCoinsHistory } from "../../api/user";

// Grey all-caps group title, same token as the sidebar / Settings sections.
function SectionLabel({ children }) {
  return (
    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary">
      {children}
    </p>
  );
}

const REWARD_META = {
  welcome_reward: { Icon: MdOutlineWavingHand, label: "Welcome to QUSHi" },
  profile_picture_reward: { Icon: MdOutlineImage, label: "Profile picture added" },
  trader_identity_reward: { Icon: MdOutlineBadge, label: "Bio and country completed" },
  daily: { Icon: MdOutlineLeaderboard, label: "Daily leaderboard" },
  weekly: { Icon: MdOutlineLeaderboard, label: "Weekly leaderboard" },
  monthly: { Icon: MdOutlineLeaderboard, label: "Monthly leaderboard" },
  contest_win: { Icon: MdOutlineEmojiEvents },
  referral_reward: { Icon: MdOutlinePersonAdd, label: "New trader joined with your code" },
  referral_bonus: { Icon: MdOutlinePersonAdd, label: "New trader joined with your code" },
};

const LEADERBOARD_TYPES = ["daily", "weekly", "monthly"];
function RewardIcon({ Icon, size }) {
  if (!Icon) return null;
  return <Icon size={size} className="shrink-0 mt-[2px] text-gray-500" />;
}

function EarnRow({ Icon, title, children }) {
  return (
    <div>
      <p className="flex items-start gap-2 text-gray-700">
        <RewardIcon Icon={Icon} size={18} />
        {title}
      </p>
      <div className="mt-1 space-y-1 text-gray-600 text-xs">{children}</div>
    </div>
  );
}

const describeEntry = (item) => {
  const meta = REWARD_META[item.type];
  if (!meta) return item.description ?? "No description";

  if (item.type === "contest_win") {
    const kind = item.sub_type === "private" ? "Private" : "Public";
    return `${kind} league · 1st place`;
  }
  if (LEADERBOARD_TYPES.includes(item.type) && item.sub_type) {
    return `${meta.label} · ${item.sub_type} place`;
  }
  return meta.label;
};

const CoinHistoryModal = ({ open, onClose }) => {
  const [coinsHistory, setCoinsHistory] = useState(null);
  const [activeTab, setActiveTab] = useState("history");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getUserCoinsHistory();
        if (!cancelled && response?.data?.success) {
          setCoinsHistory(response.data.history);
        }
      } catch (error) {
        console.error("Error fetching coin history:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      {/* Taller panel with a hidden scrollbar (.no-scrollbar in index.css).
          Scrolling still works for long histories, it just isn't drawn - and
          the pr-7 that made room for the bar is no longer needed. */}
      <div
        className="bg-white rounded-xl p-5 w-96 max-w-[92vw] max-h-[85vh] overflow-auto no-scrollbar shadow-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Tabs */}
        <div className="flex border-b mb-3 pt-2">
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 text-center py-2 font-semibold ${
              activeTab === "history"
                ? "border-b-2 border-primaryColor text-primaryColor"
                : "text-gray-500"
            }`}
          >
            Coin History
          </button>
          <button
            onClick={() => setActiveTab("earn")}
            className={`flex-1 text-center py-2 font-semibold ${
              activeTab === "earn"
                ? "border-b-2 border-primaryColor text-primaryColor"
                : "text-gray-500"
            }`}
          >
            How to Earn
          </button>
        </div>

        {/* Coin History */}
        {activeTab === "history" && (
          <>
            {(!coinsHistory || coinsHistory.length === 0) && (
              <p className="text-sm text-gray-600">No coin history available.</p>
            )}
            {coinsHistory && coinsHistory.length > 0 && (
              <div className="space-y-3">
                {coinsHistory.map((item) => {
                  const Icon = REWARD_META[item.type]?.Icon;
                  const formattedDate = new Date(item.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                  return (
                    <div
                      key={item.id}
                      className="flex flex-col border p-3 rounded-md bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-primaryColor font-bold">
                          +{Number(item.coins || 0).toLocaleString("en-US")} Coins
                        </span>
                        <span className="text-xs text-gray-500">·</span>
                        <span className="text-xs text-gray-600">
                          {formattedDate}
                        </span>
                      </div>
                      <span className="flex items-start gap-2 text-sm text-gray-800 mt-2">
                        <RewardIcon Icon={Icon} size={16} />
                        {describeEntry(item)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* How to Earn */}
        {activeTab === "earn" && (
          <div className="text-gray-800 text-sm space-y-5">
            <div>
              <SectionLabel>Get Started</SectionLabel>
              <div className="space-y-3">
                <EarnRow Icon={MdOutlineWavingHand} title="Welcome reward">
                  <p>100 Coins when you join QUSHi</p>
                </EarnRow>

                <EarnRow Icon={MdOutlineImage} title="Add a profile picture">
                  <p>100 Coins</p>
                </EarnRow>

                <EarnRow Icon={MdOutlineBadge} title="Add a bio and select your country">
                  <p>50 Coins</p>
                </EarnRow>
              </div>
            </div>

            {/* COMPETE */}
            <div>
              <SectionLabel>Compete</SectionLabel>
              <div className="space-y-3">
                <EarnRow Icon={MdOutlineLeaderboard} title="Leaderboard rewards">
                  <p>Daily: 1st 300 Coins · 2nd 200 · 3rd 100</p>
                  <p>Weekly: 1st 500 Coins · 2nd 400 · 3rd 300</p>
                  <p>Monthly: 1st 1,000 Coins · 2nd 800 · 3rd 500</p>
                </EarnRow>

                <EarnRow Icon={MdOutlineEmojiEvents} title="Finish first in a league">
                  <p>1,000 Coins for winning a public or private league</p>
                </EarnRow>
              </div>
            </div>

            <div>
              <SectionLabel>Grow</SectionLabel>
              <div className="space-y-3">
                <EarnRow Icon={MdOutlinePersonAdd} title="Refer a new trader">
                  <p>500 Coins when someone joins QUSHi using your referral code</p>
                </EarnRow>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full bg-primaryColor text-white py-2 rounded-lg"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default CoinHistoryModal;
